<template>
    <v-card class="mb-6" elevation="1" style="border-left: 4px solid var(--v-primary-base);">
        <!-- Section header — click to expand/collapse -->
        <div class="d-flex align-center pa-4" style="cursor: pointer; min-height: 64px;" @click="expanded = !expanded">
            <v-icon color="primary" class="mr-2">mdi-robot-outline</v-icon>
            <div class="flex-grow-1">
                <div class="text-h6 font-weight-bold">AI Analysis</div>
                <div class="text-caption grey--text">
                    Run an AI analysis of a team member's tracked data and screenshots for the selected date range
                    ({{ rangeLabel }})
                </div>
            </div>
            <v-chip v-if="isPolling" x-small color="orange" outlined class="mr-2">
                <v-progress-circular indeterminate size="10" width="2" class="mr-1" />
                Analyzing…
            </v-chip>
            <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>

        <v-expand-transition>
            <div v-if="expanded">
                <v-divider />
                <div class="pa-4">

            <v-row no-gutters>
                <!-- Left: run a new analysis -->
                <v-col cols="12" md="7" class="pr-md-6">
                    <v-autocomplete
                        v-model="selectedUserId"
                        :items="userItems"
                        label="Select a tracked user"
                        outlined dense clearable
                        prepend-inner-icon="mdi-account-search-outline" />

                    <div class="text-caption text-uppercase font-weight-bold grey--text mb-2">Analysis type</div>
                    <v-radio-group v-model="analysisType" class="mt-0 pt-0" hide-details>
                        <div class="d-flex flex-wrap" style="gap: 12px;">
                            <v-card v-for="mode in modes" :key="mode.value" outlined class="pa-3"
                                style="cursor: pointer; flex: 1 1 260px;"
                                :style="analysisType === mode.value ? 'border-color: var(--v-primary-base);' : ''"
                                @click="analysisType = mode.value">
                                <div class="d-flex align-start">
                                    <v-radio :value="mode.value" class="mt-0 mr-1" />
                                    <div>
                                        <div class="font-weight-bold text-body-2">
                                            <v-icon small class="mr-1">{{ mode.icon }}</v-icon>
                                            {{ mode.title }}
                                        </div>
                                        <div class="text-caption grey--text">{{ mode.description }}</div>
                                    </div>
                                </div>
                            </v-card>
                        </div>
                    </v-radio-group>

                    <v-btn color="primary" class="text-none mt-2"
                        :disabled="!selectedUserId || !analysisType"
                        :loading="isStarting || isPolling"
                        @click="runAnalysis">
                        <v-icon left>mdi-creation</v-icon>
                        {{ isPolling ? 'Analyzing…' : 'Run AI analysis' }}
                    </v-btn>
                    <div v-if="isPolling" class="text-caption grey--text mt-2">
                        <v-progress-circular indeterminate size="12" width="2" color="primary" class="mr-1" />
                        This can take a few minutes — the report will appear automatically when ready.
                    </div>
                </v-col>

                <!-- Right: previously run analyses -->
                <v-col cols="12" md="5">
                    <div class="d-flex align-center mb-2">
                        <span class="text-caption text-uppercase font-weight-bold grey--text">
                            Previous analyses{{ selectedUserName ? ` — ${selectedUserName}` : '' }}
                        </span>
                        <v-progress-circular v-if="isLoadingList" indeterminate size="14" width="2"
                            color="primary" class="ml-2" />
                    </div>

                    <div v-if="!isLoadingList && analyses.length === 0" class="grey--text text-caption">
                        No analyses have been run yet{{ selectedUserName ? ' for this user' : '' }}.
                    </div>

                    <v-list dense class="pa-0" style="max-height: 320px; overflow-y: auto;">
                        <v-list-item v-for="item in analyses" :key="item.id" class="px-2"
                            style="border: 1px solid rgba(0,0,0,0.08); border-radius: 4px; margin-bottom: 6px;"
                            @click="openAnalysis(item)">
                            <v-list-item-content class="py-2">
                                <v-list-item-title class="text-body-2 font-weight-medium">
                                    {{ typeLabel(item.analysis_type) }}
                                    <span v-if="!selectedUserId" class="grey--text">· {{ item.user_name }}</span>
                                </v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    {{ formatDate(item.date_from) }} – {{ formatDate(item.date_to) }}
                                    &nbsp;·&nbsp; run {{ formatDateTime(item.created_at) }}
                                    <span v-if="item.requested_by_name">&nbsp;·&nbsp; by {{ item.requested_by_name }}</span>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <v-chip x-small :color="statusColor(item)" outlined>
                                    {{ statusLabel(item) }}
                                </v-chip>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </v-col>
            </v-row>
                </div>
            </div>
        </v-expand-transition>

        <!-- Report dialog -->
        <v-dialog v-model="reportOpen" max-width="860" scrollable>
            <v-card v-if="report">
                <v-toolbar dense flat>
                    <v-icon class="mr-2" color="primary">mdi-robot-outline</v-icon>
                    <v-toolbar-title class="text-body-1 font-weight-medium">
                        {{ typeLabel(report.analysis_type) }} — {{ report.user_name }}
                    </v-toolbar-title>
                    <v-spacer />
                    <v-btn icon @click="reportOpen = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-toolbar>
                <v-divider />

                <v-card-text class="pt-4" style="max-height: 75vh;">
                    <!-- Meta row -->
                    <div class="d-flex align-center flex-wrap mb-4" style="gap: 8px;">
                        <v-chip small outlined>
                            <v-icon left small>mdi-calendar-range</v-icon>
                            {{ formatDate(report.date_from) }} – {{ formatDate(report.date_to) }}
                        </v-chip>
                        <v-chip small outlined>{{ report.model }}</v-chip>
                        <v-chip v-if="report.requested_by_name" small outlined>
                            <v-icon left small>mdi-account-outline</v-icon>
                            Requested by {{ report.requested_by_name }}
                        </v-chip>

                        <template v-if="report.status === 'completed' && report.result">
                            <!-- Productivity score -->
                            <v-chip v-if="report.analysis_type === 'productivity' && report.result.productivity_score !== null"
                                small color="primary">
                                Score: {{ report.result.productivity_score }}/100
                            </v-chip>
                            <!-- Risk classification -->
                            <v-chip v-if="report.analysis_type === 'activity_pattern'"
                                small :color="riskColor(report.result.risk_classification)" dark>
                                {{ riskLabel(report.result.risk_classification) }}
                            </v-chip>
                            <v-chip small outlined>Confidence: {{ report.result.confidence }}/100</v-chip>
                        </template>

                        <v-spacer />
                        <v-btn small outlined color="primary" class="text-none" @click="applyToDashboard">
                            <v-icon left small>mdi-filter-outline</v-icon>
                            View this range on the dashboard
                        </v-btn>
                    </div>

                    <!-- Failed -->
                    <v-alert v-if="report.status === 'failed'" type="error" outlined dense>
                        Analysis failed: {{ report.error || 'Unknown error' }}
                    </v-alert>
                    <!-- Still running -->
                    <v-alert v-else-if="report.status !== 'completed'" type="info" outlined dense>
                        This analysis is still running — check back shortly.
                    </v-alert>

                    <!-- Completed report -->
                    <template v-else-if="report.result">
                        <div class="report-section">
                            <div class="report-heading">Executive summary</div>
                            <ReportParagraphs :text="report.result.executive_summary" />
                        </div>

                        <!-- Productivity report body -->
                        <template v-if="report.analysis_type === 'productivity'">
                            <div class="report-section">
                                <div class="report-heading">Internal benchmark comparison</div>
                                <ReportParagraphs :text="report.result.benchmark_comparison" />
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Historical comparison</div>
                                <ReportParagraphs :text="report.result.historical_comparison" />
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Key strengths</div>
                                <ul class="text-body-2">
                                    <li v-for="(s, i) in report.result.key_strengths" :key="`s${i}`">{{ s }}</li>
                                </ul>
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Improvement opportunities</div>
                                <ul class="text-body-2">
                                    <li v-for="(s, i) in report.result.improvement_opportunities" :key="`i${i}`">{{ s }}</li>
                                </ul>
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Recommendations</div>
                                <ul class="text-body-2">
                                    <li v-for="(s, i) in report.result.recommendations" :key="`r${i}`">{{ s }}</li>
                                </ul>
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Data limitations</div>
                                <p class="text-body-2 grey--text text--darken-1 mb-0">{{ report.result.data_limitations }}</p>
                            </div>
                        </template>

                        <!-- Activity pattern report body -->
                        <template v-else>
                            <div class="report-section">
                                <div class="report-heading">Idle time analysis</div>
                                <ReportParagraphs :text="report.result.idle_time_analysis" />
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Keyboard &amp; mouse behaviour</div>
                                <ReportParagraphs :text="report.result.input_behavior_analysis" />
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Screenshot consistency</div>
                                <ReportParagraphs :text="report.result.screenshot_consistency_analysis" />
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Historical pattern comparison</div>
                                <ReportParagraphs :text="report.result.historical_comparison" />
                            </div>
                            <div v-if="report.result.anomalies && report.result.anomalies.length" class="report-section">
                                <div class="report-heading">Detected anomalies</div>
                                <v-card v-for="(a, i) in report.result.anomalies" :key="`a${i}`" outlined class="pa-3 mb-2">
                                    <div class="text-body-2 font-weight-medium">
                                        <v-icon small color="orange darken-2" class="mr-1">mdi-alert-circle-outline</v-icon>
                                        {{ a.description }}
                                    </div>
                                    <div class="text-caption grey--text text--darken-1 mt-1">
                                        Evidence: {{ a.evidence }}
                                    </div>
                                </v-card>
                            </div>
                            <div class="report-section">
                                <div class="report-heading">Why this classification</div>
                                <ReportParagraphs :text="report.result.classification_reasoning" />
                            </div>
                            <div v-if="report.result.suggested_next_steps && report.result.suggested_next_steps.length"
                                class="report-section">
                                <div class="report-heading">Suggested next steps</div>
                                <ul class="text-body-2">
                                    <li v-for="(s, i) in report.result.suggested_next_steps" :key="`n${i}`">{{ s }}</li>
                                </ul>
                            </div>
                        </template>
                    </template>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import ReportParagraphs from './ReportParagraphs.vue';

export default {
    name: 'AiAnalysisSection',

    components: { ReportParagraphs },

    props: {
        // Tracked users from the dashboard response: [{ id, name, ... }]
        users: { type: Array, default: () => [] },
        dateFrom: { type: String, required: true },
        dateTo: { type: String, required: true },
    },

    data() {
        return {
            expanded: false,
            selectedUserId: null,
            analysisType: 'productivity',
            modes: [
                {
                    value: 'productivity',
                    title: 'Productivity & Internal Benchmark',
                    icon: 'mdi-chart-line',
                    description: 'How effectively is this person working? Compares the period against their own history and teammates, with strengths, improvement areas and recommendations.',
                },
                {
                    value: 'activity_pattern',
                    title: 'Idle Time & Activity Pattern',
                    icon: 'mdi-shield-search',
                    description: 'Do these work patterns appear consistent and genuine? Reviews idle distribution, input variation and screenshots, and classifies the activity with reasoning.',
                },
            ],

            analyses: [],
            isLoadingList: false,
            isStarting: false,

            // Polling for an in-flight analysis
            pollTimer: null,
            pollingId: null,

            // Report dialog
            reportOpen: false,
            report: null,
        };
    },

    computed: {
        userItems() {
            return this.users.map(u => ({ text: u.name, value: u.id }));
        },
        selectedUserName() {
            return this.users.find(u => u.id === this.selectedUserId)?.name ?? null;
        },
        isPolling() {
            return this.pollingId !== null;
        },
        rangeLabel() {
            return `${this.formatDate(this.dateFrom)} – ${this.formatDate(this.dateTo)}`;
        },
    },

    watch: {
        selectedUserId() {
            this.loadAnalyses();
        },
    },

    mounted() {
        this.loadAnalyses();
    },

    beforeDestroy() {
        this.stopPolling();
    },

    methods: {
        async loadAnalyses() {
            this.isLoadingList = true;
            const payload = this.selectedUserId ? { user_id: this.selectedUserId } : {};
            try {
                const res = await this.$axios.get('api/agent/ai-analyses', payload);
                if (res.data?.response) {
                    this.analyses = res.data.analyses;
                }
            } catch (_) {
                // list stays as-is; not fatal
            } finally {
                this.isLoadingList = false;
            }
        },

        async runAnalysis() {
            this.isStarting = true;
            try {
                const res = await this.$axios.post('api/agent/ai-analyses', {
                    user_id: this.selectedUserId,
                    analysis_type: this.analysisType,
                    date_from: this.dateFrom,
                    date_to: this.dateTo,
                });
                if (res.data?.response) {
                    this.$toast.info('AI analysis started');
                    this.startPolling(res.data.analysis.id);
                    this.loadAnalyses();
                } else {
                    this.$toast.error(res.data?.message || 'Failed to start analysis');
                }
            } catch (e) {
                this.$toast.error(e?.response?.data?.message || e?.data?.message || 'Failed to start analysis');
            } finally {
                this.isStarting = false;
            }
        },

        startPolling(analysisId) {
            this.stopPolling();
            this.pollingId = analysisId;
            this.pollTimer = setInterval(() => this.pollAnalysis(), 5000);
        },

        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
            this.pollingId = null;
        },

        async pollAnalysis() {
            if (!this.pollingId) return;
            try {
                const res = await this.$axios.get(`api/agent/ai-analyses/${this.pollingId}`);
                if (!res.data?.response) return;

                const analysis = res.data.analysis;
                if (analysis.status === 'completed') {
                    this.stopPolling();
                    this.$toast.success('AI analysis complete');
                    this.loadAnalyses();
                    this.report = analysis;
                    this.reportOpen = true;
                } else if (analysis.status === 'failed') {
                    this.stopPolling();
                    this.$toast.error(analysis.error || 'AI analysis failed');
                    this.loadAnalyses();
                }
            } catch (_) {
                // transient poll errors are fine; keep polling
            }
        },

        async openAnalysis(item) {
            try {
                const res = await this.$axios.get(`api/agent/ai-analyses/${item.id}`);
                if (res.data?.response) {
                    this.report = res.data.analysis;
                    this.reportOpen = true;
                }
            } catch (_) {
                this.$toast.error('Failed to load analysis');
            }
        },

        // Sets the dashboard to the analysis's date range, filtered to its user.
        applyToDashboard() {
            if (!this.report) return;
            this.$emit('view-analysis', {
                user_id: this.report.user_id,
                user_name: this.report.user_name,
                date_from: this.report.date_from,
                date_to: this.report.date_to,
            });
            this.reportOpen = false;
        },

        // ---- labels / formatting ------------------------------------------

        typeLabel(type) {
            return type === 'productivity'
                ? 'Productivity & Benchmark'
                : 'Idle Time & Activity Pattern';
        },

        statusLabel(item) {
            if (item.status === 'completed') return 'Completed';
            if (item.status === 'failed') return 'Failed';
            return 'Running…';
        },

        statusColor(item) {
            if (item.status === 'completed') return 'green';
            if (item.status === 'failed') return 'red';
            return 'orange';
        },

        riskLabel(classification) {
            return {
                consistent: 'Consistent',
                review_recommended: 'Review Recommended',
                high_risk_pattern: 'High-Risk Pattern',
            }[classification] || classification;
        },

        riskColor(classification) {
            return {
                consistent: 'green',
                review_recommended: 'orange darken-2',
                high_risk_pattern: 'red darken-2',
            }[classification] || 'grey';
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr + 'T00:00:00');
            return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        },

        formatDateTime(iso) {
            if (!iso) return '';
            return new Date(iso).toLocaleString('en-GB', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
        },
    },
};
</script>

<style scoped>
.report-section {
    margin-bottom: 20px;
}
.report-heading {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);
    margin-bottom: 6px;
}
</style>
